/**
 * Hook for loading Copilot thread history.
 *
 * On mount (or when threadId changes):
 * - Fetches slim thread history (messages + stubs)
 * - Hydrates the most recent artifact only
 */

import { useEffect, useCallback, useRef, useState } from 'react';
import { getTenantIdFromStorage } from '../../../utils/onboardingLocalStorage';
import { fetchThreadList } from '../../../services/chatThreadsService';
import type { CopilotThread } from '../stores/useCopilotStore';
import {
  fetchThreadHistory,
  mapHistoryMessages,
  mapTimeline,
  stubToWorkspaceArtifact,
} from '../../../services/chatHistoryService';
import { useCopilotStore, type SQLApproval } from '../stores/useCopilotStore';
import { hydrateArtifactByRef } from './useArtifactHydration';
import { toVoiceCallSummary } from '../../../services/voiceCallService';
import { toDownloadOffer } from '../../../types/download';
import { toWorkbook } from '../../../types/workbook';

const THREAD_PAGE_SIZE = 10;

export function useThreadListLoader() {
  const [threads, setThreads] = useState<CopilotThread[]>([]);
  const [hasMore, setHasMore] = useState(false);
  const [loading, setLoading] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const loadingMoreLockRef = useRef(false);

  const loadThreads = useCallback(
    async ({ offset, replace, q }: { offset: number; replace: boolean; q?: string }) => {
      const tid = getTenantIdFromStorage();
      if (!tid) return;

      if (replace) {
        setLoading(true);
      } else if (loadingMoreLockRef.current) {
        return;
      } else {
        loadingMoreLockRef.current = true;
        setLoadingMore(true);
      }

      try {
        const data = await fetchThreadList(tid, {
          offset,
          limit: THREAD_PAGE_SIZE,
          q: q?.trim() || undefined,
        });
        setThreads((prev) => (replace ? data.threads : [...prev, ...data.threads]));
        setHasMore(data.has_more);
      } catch (e) {
        console.error('[useThreadListLoader] Error:', e);
      } finally {
        setLoading(false);
        setLoadingMore(false);
        loadingMoreLockRef.current = false;
      }
    },
    [],
  );

  return { threads, hasMore, loading, loadingMore, loadThreads };
}

export function useCopilotHistory(threadId: string | null) {
  useEffect(() => {
    if (!threadId) return;

    const initialState = useCopilotStore.getState();

    if (initialState.threadId === threadId && initialState.messages.length > 0) {
      return;
    }

    if (initialState.threadId !== threadId) {
      initialState.resetSession();
      initialState.setThreadId(threadId);
    }

    const loadHistory = async () => {
      try {
        useCopilotStore.getState().setIsLoadingHistory(true);
        const tid = getTenantIdFromStorage();
        if (!tid) return;

        const data = await fetchThreadHistory(tid, threadId);
        const post = useCopilotStore.getState();
        if (post.threadId !== threadId) return;
        if (post.messages.length > 0) return;

        const store = useCopilotStore.getState();
        const findingRefs = new Set(
          (data.artifacts || []).filter((stub) => stub.type === 'finding').map((stub) => stub.ref),
        );
        const artifacts = (data.artifacts || [])
          .filter((stub) => stub.type !== 'finding')
          .map(stubToWorkspaceArtifact);
        const auditStubs = Object.fromEntries(
          (data.audits || []).map((a) => [a.event_id, a]),
        );
        const lastArtifactId =
          artifacts.length > 0 ? artifacts[artifacts.length - 1].id : null;

        if (data.thread_title) {
          store.setThreadTitle(data.thread_title);
        }

        store.setThreadOwnership({
          ownerUserId: data.owner_user_id ?? null,
          ownerName: data.owner_name ?? null,
          ownerEmail: data.owner_email ?? null,
          isOwner: typeof data.is_owner === 'boolean' ? data.is_owner : null,
        });

        store.setThreadPlaybookContext({
          agentType: data.agent_type ?? null,
          playbookId: data.playbook_id ?? null,
          playbookRunId: data.playbook_run_id ?? null,
          playbookName: data.playbook_name ?? null,
        });

        store.loadHistoryShell({
          messages: mapHistoryMessages(data.messages || []),
          artifacts,
          findings: data.findings || [],
          timeline: mapTimeline(data.timeline || []).filter(
            (entry) => entry.type !== 'artifact' || !findingRefs.has(entry.artifactId),
          ),
          auditStubs,
          activeTabId: lastArtifactId,
        });

        if (data.pending_sql_approval) {
          const p = data.pending_sql_approval;
          const approval: SQLApproval = {
            sql: p.sql,
            title: p.title,
            connectorId: p.connector_id,
            connectorName: p.connector_name,
            connectorType: p.connector_type,
            connectorCategory: p.connector_category,
            isFileConnector: p.is_file_connector,
            isApiConnector: p.connector_category === 'api',
            entityId: p.entity_id,
            entityName: p.entity_name,
            columns: p.columns,
            resource: p.resource,
            properties: p.properties,
            limit: p.limit ?? null,
            timelineId: p.timeline_id,
          };
          store.setPendingSQLApproval(approval);
        }

        // Receipts for calls already made on this thread, so their cards
        // render after a reload. The transcript isn't in here — the card
        // fetches the full call only if someone opens it.
        for (const raw of data.voice_calls || []) {
          store.addVoiceCallSummary(toVoiceCallSummary(raw));
        }

        // Downloads the agent offered earlier in this thread. The card
        // re-checks the frame on click, so an offer whose session has since
        // expired fails loudly rather than handing back an empty file.
        for (const raw of data.downloads || []) {
          store.addDownloadOffer(toDownloadOffer(raw));
        }

        // Workbooks the agent built. Their bytes are stored, so unlike a
        // download offer these are still good however old the thread is.
        for (const raw of data.workbooks || []) {
          store.addWorkbook(toWorkbook(raw));
        }

        // A call the agent asked to make but nobody answered yet. Without
        // this, reloading the page leaves the graph paused on an interrupt
        // with nothing on screen able to resume it.
        if (data.pending_voice_call_approval) {
          const c = data.pending_voice_call_approval;
          store.setPendingVoiceCallApproval({
            carrierName: c.carrier_name || 'the carrier',
            phoneNumber: c.phone_number || '',
            objective: c.objective || '',
            policyNumber: c.policy_number || undefined,
            policyholder: c.policyholder || undefined,
            timelineId: c.timeline_id,
          });
        }

        if (lastArtifactId) {
          await hydrateArtifactByRef(lastArtifactId);
        }
      } catch (e) {
        console.error('[useCopilotHistory] Error loading history:', e);
      } finally {
        useCopilotStore.getState().setIsLoadingHistory(false);
      }
    };

    void loadHistory();
  }, [threadId]);
}
